/**
 * Shared popup content component for place markers
 * Used by both PlacePopup (AncientWorldMap) and PlaceMarkerPopup (PassageMap)
 */

import { useState, useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { ImportanceBadge } from '@/components/ui/importance-badge';
import { ConfidenceBadge } from '@/components/ui/confidence-badge';
import { PrecisionBadge } from '@/components/ui/precision-badge';
import { PlaceTypeBadge } from '@/components/ui/place-type-badge';
import { SourceLinks } from '@/components/ui/source-links';
import { ExternalLink, Map, ChevronRight, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AIDisclaimerBadge } from '@/components/ui/ai-disclaimer-badge';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import { getStandardPeriod, PERIOD_DEFINITIONS } from '@/lib/utils/historical-periods-shared';

interface PlacePopupContentProps {
  placeName: string;
  type: string;
  summary?: string;
  description?: string;
  biblicalRelevance?: string;
  passageRelevance?: string;
  modernReference?: string;
  coordinates?: {
    precision?: string;
    confidence?: string;
  };
  importance?: Importance;
  geography?: Place['geography'];
  significance?: Place['significance'];
  archaeology?: Place['archaeology'];
  historicalPeriods?: Place['historicalPeriods'];
  historicalContext?: GeographicEntity['historicalContext'];
  biblicalReferences?: Place['biblicalReferences'];
  historicalMaps?: Place['historicalMaps'];
  relatedHistoricalEvents?: string[];
  sources?: Source[];
  verseRef?: string;
  bookName?: string;
  relevanceType?: string;
  relevanceReason?: string;
  // Place images (from hydrated Place data)
  ruinsImageUrl?: string;
  reconstructionsByPeriod?: Place['images']['reconstructionsByPeriod'];
  // Passage year for period-specific image selection
  passageYear?: number | null;
  generatedBy?: string;
  hideAIDisclaimer?: boolean;
}

export function PlacePopupContent(props: PlacePopupContentProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isDarkMode = useDarkMode();

  // Select the best reconstruction image based on passage year
  const selectedReconstructionUrl = useMemo(() => {
    if (!props.reconstructionsByPeriod) return undefined;

    const availablePeriods = Object.keys(props.reconstructionsByPeriod) as HistoricalPeriod[];
    if (availablePeriods.length === 0) return undefined;

    // If we have a passage year, find the matching period
    if (props.passageYear !== null && props.passageYear !== undefined) {
      const matchingPeriod = getStandardPeriod(props.passageYear);

      // Try exact match first
      if (matchingPeriod && availablePeriods.includes(matchingPeriod)) {
        return props.reconstructionsByPeriod[matchingPeriod]?.imageUrl;
      }

      // Find the closest available period to the passage year
      let closestPeriod: HistoricalPeriod | null = null;
      let smallestDiff = Infinity;

      availablePeriods.forEach(period => {
        const periodDef = PERIOD_DEFINITIONS[period];
        if (!periodDef) return;

        // Calculate distance from passage year to this period's range
        let diff: number;
        if (props.passageYear! < periodDef.start) {
          // Passage year is before this period
          diff = periodDef.start - props.passageYear!;
        } else if (props.passageYear! > periodDef.end) {
          // Passage year is after this period
          diff = props.passageYear! - periodDef.end;
        } else {
          // Passage year is within this period (shouldn't happen since we checked exact match)
          diff = 0;
        }

        if (diff < smallestDiff) {
          smallestDiff = diff;
          closestPeriod = period;
        }
      });

      if (closestPeriod) {
        return props.reconstructionsByPeriod[closestPeriod]?.imageUrl;
      }
    }

    // Final fallback: Most recent period (if no passage year provided)
    const sortedPeriods = availablePeriods.sort((a, b) => {
      const aEnd = PERIOD_DEFINITIONS[a]?.end || 0;
      const bEnd = PERIOD_DEFINITIONS[b]?.end || 0;
      return bEnd - aEnd; // Descending order (most recent first)
    });

    return props.reconstructionsByPeriod[sortedPeriods[0]]?.imageUrl;
  }, [props.reconstructionsByPeriod, props.passageYear]);

  const hasAnyImages = props.ruinsImageUrl || selectedReconstructionUrl;

  return (
    <div className={`space-y-3 w-full sm:w-[320px] min-h-[120px] sm:max-h-[70vh] sm:overflow-y-auto ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
      {/* Header */}
      <div>
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-col">
            <h3 className="font-semibold text-base">{props.placeName}</h3>
            {props.verseRef && (
              <Badge variant="outline" className={`text-[10px] w-fit mt-1 ${isDarkMode ? 'text-gray-300 border-gray-600' : 'text-gray-600 border-gray-300'}`}>
                {`${props.bookName} ${props.verseRef}`}
              </Badge>
            )}
          </div>
        </div>

        {/* Summary */}
        {props.summary && (
          <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            {props.summary}
          </div>
        )}

        {/* Images Section */}
        {hasAnyImages && (
          <div className="space-y-2 pt-2">
            <div className="grid grid-cols-2 gap-2">
              {props.ruinsImageUrl && (
                <div className="space-y-1">
                  <img
                    src={props.ruinsImageUrl}
                    alt="Ruins"
                    className={`w-full h-20 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity border ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}
                    onClick={() => window.open(props.ruinsImageUrl, '_blank')}
                  />
                </div>
              )}
              {selectedReconstructionUrl && (
                <div className="space-y-1">
                  <img
                    src={selectedReconstructionUrl}
                    alt="Reconstruction"
                    className={`w-full h-20 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity border ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}
                    onClick={() => window.open(selectedReconstructionUrl, '_blank')}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Description - Expanded view */}
        {props.description && (
          <div className={`space-y-1 p-2 rounded ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
            <p className={`text-[10px] font-semibold uppercase tracking-wide ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>Historical & Geographic Context</p>
            <p className={`text-sm leading-relaxed whitespace-pre-line ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
              {props.description}
            </p>
          </div>
        )}

        {/* Biblical Relevance or Passage Relevance - Expanded view */}
        {(props.biblicalRelevance || props.passageRelevance) && (
          <div className={`space-y-1 p-2 rounded ${isDarkMode ? 'bg-blue-950/30' : 'bg-blue-50'}`}>
            <p className={`text-[10px] font-semibold uppercase tracking-wide ${isDarkMode ? 'text-blue-200' : 'text-blue-900'}`}>
              {props.passageRelevance ? 'Passage Relevance' : 'Biblical Relevance'}
            </p>
            <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-blue-100' : 'text-blue-900'}`}>
              {props.passageRelevance || props.biblicalRelevance}
            </p>
          </div>
        )}

        <div className="flex gap-1 mt-1 flex-wrap">
          <PlaceTypeBadge type={props.type} />
          {props.coordinates?.precision && (
            <PrecisionBadge
              precision={props.coordinates.precision}
              context="coordinates"
            />
          )}
          {props.coordinates?.confidence && (
            <ConfidenceBadge
              confidence={props.coordinates.confidence}
              context="coordinates"
            />
          )}
          {props.importance && (
            <ImportanceBadge
              importance={props.importance}
              context="place"
            />
          )}
          {props.relevanceType && props.relevanceType !== 'passage-place' && (
            <Badge variant="outline" className={`text-xs ${isDarkMode ? 'text-gray-300 border-gray-600' : 'text-gray-700 border-gray-300'}`}>
              {props.relevanceReason}
            </Badge>
          )}
        </div>
      </div>

      {/* Expand/Collapse button */}
      <Button
        onClick={() => setIsExpanded(!isExpanded)}
        variant="secondary"
        className="text-xs w-full"
      >
        {isExpanded ? (
          <>
            Show less
            <ChevronDown className="h-3 w-3" />
          </>
        ) : (
          <>
            Show more
            <ChevronRight className="h-3 w-3" />
          </>
        )}
      </Button>


      {/* Modern Reference */}
      {isExpanded && props.modernReference && (
        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          <span className="font-semibold">Modern:</span> {props.modernReference}
        </p>
      )}
      {/* Geography - Expanded view */}
      {isExpanded && props.geography && (
        <div className={`border-t pt-2 space-y-1 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <p className={`text-xs font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Geography:</p>
          {props.geography.terrain && <p className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}><span className="font-semibold">Terrain:</span> {props.geography.terrain}</p>}
          {props.geography.elevation && <p className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}><span className="font-semibold">Elevation:</span> {props.geography.elevation}</p>}
          {props.geography.waterSources && props.geography.waterSources.length > 0 && (
            <p className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}><span className="font-semibold">Water:</span> {props.geography.waterSources.join(', ')}</p>
          )}
          {props.geography.climate && <p className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}><span className="font-semibold">Climate:</span> {props.geography.climate}</p>}
          {props.geography.strategicImportance && <p className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}><span className="font-semibold">Strategic Importance:</span> {props.geography.strategicImportance}</p>}
        </div>
      )}

      {/* Significance - Expanded view */}
      {isExpanded && props.significance && (
        <div className={`border-t pt-2 space-y-1 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <p className={`text-xs font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Significance:</p>
          {props.significance.religious && (
            <div className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <span className="font-semibold">Religious:</span> {props.significance.religious}
            </div>
          )}
          {props.significance.political && (
            <div className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <span className="font-semibold">Political:</span> {props.significance.political}
            </div>
          )}
          {props.significance.economic && (
            <div className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <span className="font-semibold">Economic:</span> {props.significance.economic}
            </div>
          )}
          {props.significance.military && (
            <div className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <span className="font-semibold">Military:</span> {props.significance.military}
            </div>
          )}
        </div>
      )}

      {/* Archaeology - Expanded view */}
      {isExpanded && props.archaeology && (
        <div className={`border-t pt-2 space-y-1 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <p className={`text-xs font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Archaeology:</p>
          {props.archaeology.modernSiteName && <p className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}><span className="font-semibold">Modern Site:</span> {props.archaeology.modernSiteName}</p>}
          {props.archaeology.excavations && (
            <div className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <span className="font-semibold">Excavations:</span> {props.archaeology.excavations}
            </div>
          )}
          {props.archaeology.keyFindings && (
            <div className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <span className="font-semibold">Key Findings:</span> {props.archaeology.keyFindings}
            </div>
          )}
          {props.archaeology.preservationStatus && <p className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}><span className="font-semibold">Preservation:</span> {props.archaeology.preservationStatus}</p>}
        </div>
      )}

      {/* Historical Periods or Historical Context - Expanded view */}
      {isExpanded && ((props.historicalPeriods && props.historicalPeriods.length > 0) || (props.historicalContext && props.historicalContext.length > 0)) && (
        <div className={`border-t pt-2 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <p className={`text-xs font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Historical {props.historicalPeriods ? 'Periods' : 'Contexts'}:</p>
          <div className="space-y-3">
            {props.historicalPeriods && props.historicalPeriods.map((period, idx) => {
              // Get the reconstruction image for this period
              const periodImageUrl = props.reconstructionsByPeriod?.[period.period]?.imageUrl;

              return (
                <div key={idx} className={`text-xs space-y-1 p-2 rounded ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                  {/* Period Header */}
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{period.period}</span>
                    <Badge variant="outline" className={`text-[10px] px-1 py-0 ${isDarkMode ? 'text-gray-300 border-gray-600' : 'text-gray-700 border-gray-300'}`}>
                      {period.dates}
                    </Badge>
                  </div>

                  {/* Era Reconstruction Image */}
                  {periodImageUrl && (
                    <div className="w-full my-1 h-20">
                      <img
                        src={periodImageUrl}
                        alt={`${props.placeName} during ${period.period}`}
                        className={`w-full h-20 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
                        onClick={() => window.open(periodImageUrl, '_blank')}
                        loading="lazy"
                        onError={(e) => {
                          // Hide image if it fails to load
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  )}

                  {/* Description & Significance */}
                  {period.description && (
                    <p className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{period.description}</p>
                  )}
                  {period.significance && (
                    <p className={`text-xs italic ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{period.significance}</p>
                  )}

                  {/* Demographics & Governance */}
                  {(period.population || (period.rulers && period.rulers.length > 0)) && (
                    <div className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {period.population && (
                        <div><span className="font-semibold">Population:</span> {period.population}</div>
                      )}
                      {period.rulers && period.rulers.length > 0 && (
                        <div><span className="font-semibold">Rulers:</span> {period.rulers.join(', ')}</div>
                      )}
                    </div>
                  )}

                  {/* Physical Characteristics */}
                  {(period.citySize || period.fortifications || (period.gates && period.gates.length > 0) || (period.majorStructures && period.majorStructures.length > 0)) && (
                    <div className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {period.citySize && (
                        <div><span className="font-semibold">City Size:</span> {period.citySize}</div>
                      )}
                      {period.fortifications && (
                        <div><span className="font-semibold">Fortifications:</span> {period.fortifications}</div>
                      )}
                      {period.gates && period.gates.length > 0 && (
                        <div><span className="font-semibold">Gates:</span> {period.gates.join(', ')}</div>
                      )}
                      {period.majorStructures && period.majorStructures.length > 0 && (
                        <div><span className="font-semibold">Major Structures:</span> {period.majorStructures.join(', ')}</div>
                      )}
                    </div>
                  )}

                  {/* Economic & Trade */}
                  {(period.economy || (period.tradeGoods && period.tradeGoods.length > 0)) && (
                    <div className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {period.economy && (
                        <div><span className="font-semibold">Economy:</span> {period.economy}</div>
                      )}
                      {period.tradeGoods && period.tradeGoods.length > 0 && (
                        <div><span className="font-semibold">Trade Goods:</span> {period.tradeGoods.join(', ')}</div>
                      )}
                    </div>
                  )}

                  {/* Cultural Influence */}
                  {period.culturalInfluence && (
                    <div className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <span className="font-semibold">Cultural Influence:</span> {period.culturalInfluence}
                    </div>
                  )}
                </div>
              );
            })}
            {props.historicalContext && props.historicalContext.map((ctx, idx) => {
              // Get the reconstruction image for this period
              const periodImageUrl = props.reconstructionsByPeriod?.[ctx.period]?.imageUrl;

              return (
                <div key={idx} className={`text-xs space-y-1 p-2 rounded ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{ctx.eraLabel}</span>
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                      {ctx.period}
                    </Badge>
                    <Badge variant="outline" className={`text-[10px] px-1 py-0 ${isDarkMode ? 'text-gray-300 border-gray-600' : 'text-gray-700 border-gray-300'}`}>
                      {ctx.displayDateRange}
                    </Badge>
                  </div>

                  {/* Era Reconstruction Image */}
                  {periodImageUrl && (
                    <div className="w-full my-1 h-20">
                      <img
                        src={periodImageUrl}
                        alt={`${props.placeName} during ${ctx.period}`}
                        className={`w-full h-20 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
                        onClick={() => window.open(periodImageUrl, '_blank')}
                        loading="lazy"
                        onError={(e) => {
                          // Hide image if it fails to load
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  )}

                  <p className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{ctx.mapFact}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Biblical References - Expanded view */}
      {isExpanded && props.biblicalReferences && props.biblicalReferences.length > 0 && (
        <div className={`border-t pt-2 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <p className={`text-xs font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Biblical References:</p>
          <div className="space-y-1">
            {props.biblicalReferences.map((ref, idx) => (
              <div key={idx} className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <span className={`font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{ref.book}:</span> {ref.context}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Related Historical Events */}
      {isExpanded && props.relatedHistoricalEvents && props.relatedHistoricalEvents.length > 0 && (
        <div className={`border-t pt-2 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <p className={`text-xs font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Related Events:</p>
          <div className="flex flex-wrap gap-1">
            {props.relatedHistoricalEvents.map((event, idx) => (
              <Badge key={idx} variant="outline" className={`text-xs ${isDarkMode ? 'text-gray-300 border-gray-600' : 'text-gray-700 border-gray-300'}`}>
                {typeof event === 'string' ? event : event}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Historical Maps - Expanded view */}
      {isExpanded && props.historicalMaps && props.historicalMaps.length > 0 && (
        <div className={`border-t pt-2 space-y-2 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <p className={`text-xs font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Historical Maps:</p>
          {props.historicalMaps.map((map, index) => (
            <div key={index} className="text-xs space-y-1">
              <div className="flex items-center gap-1">
                <Map className={`h-3 w-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                <a
                  href={`https://www.google.com/search?q=${encodeURIComponent(map.mapSearchTerms)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`font-medium hover:underline inline-flex items-center gap-0.5 ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
                >
                  {map.mapName}
                  <ExternalLink className="h-2.5 w-2.5" />
                </a>
              </div>
              <p className={`ml-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{map.description}</p>
              {map.sources && map.sources.length > 0 && (
                <div className="ml-4">
                  <SourceLinks sources={map.sources} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Sources - Expanded view */}
      {props.sources && props.sources.length > 0 && (
        <div className={`border-t pt-2 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <SourceLinks sources={props.sources} />
        </div>
      )}

      {/* AI Disclaimer at the very end */}
      {!props.hideAIDisclaimer && (
        <div className="pt-4 mt-4 flex justify-center border-t border-border/10">
          <AIDisclaimerBadge
            model={props.generatedBy}
            contentId={props.placeName}
            contextType="place"
          />
        </div>
      )}
    </div>
  );
}

